import { addMonths, addYears, format } from 'date-fns';
import { IOptionAnyValue } from 'bricks/types';
import { IncomeCalculationType, ProFormaModelIds } from 'constants/proForma';
import { isAllApartmentModels, isStudentHousingModel } from 'helpers/proForma';
import { formatIsoDate, parseIsoDate } from 'ns_libs/formatter';
import {
    IDispositionData, IOperatingInfo, IProFormaGeneralInfo, SaleFrequency, SaleProceeds,
} from '../../../types';
import { ProFormaGenericRow } from '../../ProFormaGenericTable/types';
import {
    APARTMENT_UNIT_TYPE_NAME, UnitTypeOptionsApartment,
    UnitTypeOptionsNonApartmentEstimate, UnitTypeOptionsNonApartmentTenants,
    NON_APARTMENT_UNIT_TYPE_NAME, UnitTypeOptionsDataCenter,
    STUDENT_HOUSING_UNIT_TYPE_NAME, UnitTypeOptionsStudentHousing,
    YEARS_AFTER_DEV_END,
} from './constants';

// region: Sales-start option helpers (used by ProFormaExitSettings)

export interface SalesStartOption {
    value: string;
    label: string;
}

export const buildSalesStartOptions = (
    constructionStart: string | null | undefined,
    developmentEndDate: string | null | undefined,
    frequency: SaleFrequency,
): SalesStartOption[] => {
    if (!constructionStart || !developmentEndDate) return [];

    const monthStep = frequency === 'quarterly' ? 3 : 1;
    const prefix = frequency === 'quarterly' ? 'QTR' : 'MO';
    const csDate = parseIsoDate(constructionStart);
    const devEndDate = parseIsoDate(developmentEndDate);
    if (!csDate || !devEndDate) return [];

    const rangeEnd = addYears(devEndDate, YEARS_AFTER_DEV_END);
    const options: SalesStartOption[] = [];

    let current = addMonths(new Date(csDate.getFullYear(), csDate.getMonth(), 1), monthStep);
    let index = 1;
    while (current <= rangeEnd) {
        options.push({ value: formatIsoDate(current), label: `${prefix} ${index} - ${format(current, 'MMM yyyy')}` });
        current = addMonths(current, monthStep);
        index += 1;
    }
    return options;
};

export const resolveSalesStartOption = (
    options: SalesStartOption[],
    salesStart: string | null | undefined,
): SalesStartOption | null => options.find(option => option.value === salesStart) ?? options[0] ?? null;

// endregion

export const getColumnTitle = (key: string): string => {
    switch (key) {
        case 'untrended':
            return 'Untrended';
        case 'trended':
            return 'Trended';
        case 'sale':
            return 'Sale';
        default:
            return '';
    }
};

export const getDispositionPeriodSubtitle = (key: string, disposition?: IDispositionData): string => {
    if (!disposition?.trendedPeriod?.start || !disposition?.salePeriod?.start) return '';

    switch (key) {
        case 'untrended':
            return '(Today)';
        case 'trended':
            return `(Period ${disposition?.trendedPeriod?.start} - ${disposition?.trendedPeriod?.end})`;
        case 'sale':
            return `(Period ${disposition?.salePeriod?.start} - ${disposition?.salePeriod?.end})`;
        default:
            return '';
    }
};

export const getSaleProceedsData = (disposition: IDispositionData | undefined, key: string): SaleProceeds | undefined => {
    const defaultValues = {
        amount: 0,
        perUnit: 0,
        perSf: 0,
        perGrossSf: 0,
        perBed: 0,
        perKw: 0,
        perMw: 0,
    };
    if (!disposition) {
        return defaultValues;
    }
    const dispositionData = disposition as Record<string, any>;
    const data = dispositionData[key] as SaleProceeds;
    if (data && data.perUnit === null && data.perSf === null && data.amount === null && data.perGrossSf === null
        && (data.perBed === null || data.perBed === undefined)) {
        return defaultValues;
    }
    return data;
};

export const createRow = (id: string, title: string, data: SaleProceeds | undefined | null, selectedType: string): ProFormaGenericRow => {
    const baseRow = {
        id,
        title,
        amount: data?.amount !== undefined && data?.amount !== null ? String(data.amount) : '0',
        isReadOnly: true,
    };

    let value = data?.perSf;
    if (selectedType === APARTMENT_UNIT_TYPE_NAME.UNIT) {
        value = data?.perUnit;
    } else if (selectedType === STUDENT_HOUSING_UNIT_TYPE_NAME.BED) {
        value = data?.perBed;
    } else if (selectedType === NON_APARTMENT_UNIT_TYPE_NAME.GSF) {
        value = data?.perGrossSf;
    } else if (selectedType === NON_APARTMENT_UNIT_TYPE_NAME.KW) {
        value = data?.perKw;
    } else if (selectedType === NON_APARTMENT_UNIT_TYPE_NAME.MW) {
        value = data?.perMw;
    } else if (selectedType === NON_APARTMENT_UNIT_TYPE_NAME.RACK) {
        value = data?.perRack;
    }

    return {
        ...baseRow,
        valueByType: value !== undefined && value !== null ? String(value) : '0',
    };
};

export const getUnitTypeOptions = (
    operatingInfo: IOperatingInfo,
    proForma: IProFormaGeneralInfo,
) => {
    if (isStudentHousingModel(proForma?.proFormaModelId)) {
        return UnitTypeOptionsStudentHousing;
    }
    if (isAllApartmentModels(proForma?.proFormaModelId)) {
        return UnitTypeOptionsApartment;
    }

    let nonApartmentTypeOptions: IOptionAnyValue[] = [];
    if (operatingInfo.incomeCalculationType === IncomeCalculationType.ESTIMATE) {
        nonApartmentTypeOptions = UnitTypeOptionsNonApartmentEstimate;
    }
    if (operatingInfo.incomeCalculationType === IncomeCalculationType.TENANTS) {
        nonApartmentTypeOptions = UnitTypeOptionsNonApartmentTenants;
    }
    if (proForma?.proFormaModelId === ProFormaModelIds.DATA_CENTER) {
        nonApartmentTypeOptions = [
            ...nonApartmentTypeOptions,
            ...UnitTypeOptionsDataCenter,
        ];
    }

    return nonApartmentTypeOptions;
};
